// src/api/apiClient.js

import { API_BASE_URL } from "../utils/constants/api_constants";
import LocalStorageService from "../infrastucture/internal/LocalStorageService";
import { getAccessTokenExpiryDateTime } from '../utils/dateUtils';
import UnauthorizedError from '../domain/models/UnauthorizedError'

const localStorageService = LocalStorageService.getService();



class ApiClient {
  constructor(baseURL) {
    this.baseURL = baseURL;
    this.isRefreshing = false;
    this.logoutCallback = null; // Store logout callback
  }

  setLogoutCallback(callback) {
    this.logoutCallback = callback;
  }

  async sendRequest(endpoint, method = "GET", body = null) {
    const headers = {
      "Content-Type": "application/json",
    };
    const token = localStorageService.getAccessToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const options = {
      method: method.toUpperCase(),
      headers: headers,
      body: body ? JSON.stringify(body) : null,
    };

    // console.log("Url to send is " + `${this.baseURL}/${endpoint}`);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 60000); // 60 seconds timeout

    const response = await fetch(`${this.baseURL}/${endpoint}`, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timeout);

    if (response.status == 204) {
      return;
    }
    if (response.status == 401) {
      const refreshed = await this.refreshAccessToken();
      if (refreshed) {
        const data = this.reSendRequest(endpoint, method, body)
        return data;
      }
      else {
        if (this.logoutCallback) {
          this.logoutCallback();
        } else {
          // Fallback if callback not set
          localStorageService.removeAccessToken();
          localStorageService.removeRefreshToken();
          localStorageService.removeATExpiresIn();
          window.location.href = '/login';
        }
        throw new UnauthorizedError('Unauthorized access');
      }
    }
    if (!response.ok) {
      const errorBody = await response.text();
      console.error(`Request to ${endpoint} failed: ${response.status} - ${errorBody}`);
      throw new Error(`Request to ${endpoint} failed: ${response.status} - ${errorBody}`);
    }

    const data = await response.json();
    return data;
  }

  async reSendRequest(endpoint, method = "GET", body = null) {
    const headers = {
      "Content-Type": "application/json",
    };
    const token = localStorageService.getAccessToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    else {
      throw new UnauthorizedError('Unauthorized access');
    }

    const options = {
      method: method.toUpperCase(),
      headers: headers,
      body: body ? JSON.stringify(body) : null,
    };

    const response = await fetch(`${this.baseURL}/${endpoint}`, options);

    if (!response.ok) {
      throw new Error(`Request failed: ${response.status}`);
    }
    if (response.status == 204) {
      return;
    }
    if (response.status == 401) {
      throw new UnauthorizedError('Unauthorized access');
    }
    const data = await response.json();
    return data;
  }

  async get(endpoint) {
    return this.sendRequest(endpoint, "GET");
  }

  async post(endpoint, body) {
    return this.sendRequest(endpoint, "POST", body);
  }

  async put(endpoint, body) {
    return this.sendRequest(endpoint, "PUT", body);
  }

  async patch(endpoint, body) {
    return this.sendRequest(endpoint, "PATCH", body);
  }

  async delete(endpoint) {
    return this.sendRequest(endpoint, "DELETE");
  }

  async refreshAccessToken() {
    if (this.isRefreshing) {
      // Wait for the ongoing refresh to complete
      await new Promise(resolve => setTimeout(resolve, 100));
      return localStorageService.getAccessToken() !== null;
    }
    this.isRefreshing = true;
    try {
      const refreshToken = localStorageService.getRefreshToken();
      if (!refreshToken) return false;

      const response = await fetch(`${this.baseURL}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken, mode: "json" }),
      });

      if (response.status === 401) return false;
      if (!response.ok) throw new Error("Refresh failed");

      const { data } = await response.json();
      const { access_token, expires, refresh_token } = data;
      const expiresIn = getAccessTokenExpiryDateTime(expires);
      localStorageService.setAccessToken(access_token);
      localStorageService.setRefreshToken(refresh_token);
      localStorageService.setATExpiresIn(expiresIn);
      return true;
    } catch (error) {
      console.error("Token refresh failed:", error);
      return false;
    } finally {
      this.isRefreshing = false;
    }
  }

}

const apiClient = new ApiClient(API_BASE_URL);
export default apiClient;
